import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Search, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { NotesPanel } from '@/components/app/NotesPanel';
import { Badge } from '@/components/ui/badge';
import { KYC_STATUS_LABEL, KYC_STATUS_COLOR } from '@/lib/constants';

export default function ConsultantTenants() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [notesFor, setNotesFor] = useState(null);
  const [form, setForm] = useState({ name: '', mobile: '', email: '', kyc_id: '', emergency_contact: '' });

  const load = () => api.get('/tenants', { params: { q: q || undefined } }).then(r => setItems(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [q]);

  const submit = async () => {
    try {
      await api.post('/tenants', form);
      toast.success('Tenant added');
      setOpen(false); setForm({ name: '', mobile: '', email: '', kyc_id: '', emergency_contact: '' }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div>
      <PageHeader title="Tenants" subtitle="Manage tenant master data" actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-tenant-button"><Plus className="h-4 w-4 mr-1" /> Add tenant</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add tenant</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Full name</Label><Input data-testid="tenant-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>KYC ID (PAN)</Label><Input value={form.kyc_id} onChange={(e) => setForm({ ...form, kyc_id: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Emergency contact</Label><Input value={form.emergency_contact} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="tenant-submit-button">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      } />
      <div className="mb-3 max-w-md relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input className="pl-8" placeholder="Search by name, mobile, email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tenants-search" />
      </div>
      <Card className="p-0 overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-muted/60"><tr><th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Name</th><th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Mobile</th><th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Email</th><th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">KYC</th><th className="px-3 py-2 text-xs uppercase text-muted-foreground text-right">Actions</th></tr></thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id} className="border-t hover:bg-muted/40">
                <td className="px-3 py-2 font-medium">{t.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{t.mobile}</td>
                <td className="px-3 py-2">{t.email}</td>
                <td className="px-3 py-2"><Badge variant="outline" className={KYC_STATUS_COLOR[t.kyc_status] || ''}>{KYC_STATUS_LABEL[t.kyc_status] || t.kyc_status || 'pending'}</Badge></td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setNotesFor(t)} data-testid={`tenant-notes-${t.id}`}><MessageSquare className="h-3.5 w-3.5 mr-1" /> Notes</Button>
                </td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No tenants yet.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!notesFor} onOpenChange={(v) => !v && setNotesFor(null)}>
        <DialogContent className="!max-w-2xl max-h-[92dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Notes — {notesFor?.name}</DialogTitle>
          </DialogHeader>
          {notesFor && <NotesPanel tenantId={notesFor.id} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
